import * as React from 'react';
import * as classnames from 'classnames';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { ProductSizeBox } from '../common/ProductSizeBox';
import { Size } from '../../domain/entities/Size';
import {
    sizeLabelTheme,
    sizePickerLabelTheme,
    sizeLabelThemeDropdown,
    sizePickerLabelThemeDropdown
} from '../../../theme/default-theme';
import { breakpoint, Breakpoints, ContentSpacing } from 'elc-base-theme';
import { ISliderSettings, SliderView } from 'elc-slider';
import { Dropdown } from 'elc-dropdown';
import { Body1, Body2, Subtitle2 } from 'elc-typography';
import { translate } from 'elc-service';
import { Column } from 'elc-grid';
import { ProductImages } from 'elc-images';
import { PRODUCT_SIZE_DROPDOWN } from '../../constants/DataTestIdAttributes';
import { Asset } from '../../domain/entities/Asset';
import { Desktop, Mobile } from 'elc-layouts';
import { SIZE_PICKER_TYPE } from '../../../internal/constants/SizePickerTypes';
import { classes } from '../../../internal/utils/Classes';

export enum SIZE_PICKER_VIEW {
    PRODUCT_GRID = 'product_grid',
    PRODUCT_QUICK_SHOP = 'product_quickshop',
    PRODUCT_FULL = 'product_full',
    PRODUCT_ADD_TO_BAG = 'product_sticky_add_to_bag'
}

type SizePickerConfigs = { [K in SIZE_PICKER_VIEW]: ISizePickerOptions };
export interface ISizePickerConfigs extends SizePickerConfigs {
    [K: string]: ISizePickerOptions;
}

export interface ISizePickerOptions {
    enabled?: boolean;
    type?: string;
    showThumbIfSingleSize?: boolean;
    showPrice?: boolean;
    hidePricePerUnit?: boolean;
    carousel?: ISliderSettings;
}

export interface IProductSizePickerLabels {
    sizeLabel: string;
    selectASizeLabel: string;
}

export interface ISizePicker {
    sizes: Size[];
    selectedSize: Size;
    onSelect?(name: string, productId: string): void;
    translations?: IProductSizePickerLabels;
    useDropup?: boolean;
    hideSizeWhenSingular?: boolean;
    hasMultipleSizes?: boolean;
    isSized?: boolean;
    type?: SIZE_PICKER_TYPE;
    productId: string;
    options?: ISizePickerOptions;
}
const SizesDropdown = styled(Dropdown)`
    ${breakpoint('desktop')`
        margin-bottom: ${ContentSpacing.space16};

        .elc-dropdown-options {
            z-index: 20;
        }
    `};
`;

const SizeDropdownBase = styled(Body1)`
    display: flex;
    justify-content: center;
    ${sizeLabelThemeDropdown};
`;

const SizeDropdown = SizeDropdownBase.withComponent('span');

const LabelDropdown = styled(Body2)`
    white-space: nowrap;
    padding: 2px ${ContentSpacing.space4};
    ${sizePickerLabelThemeDropdown};
`;

const SpaceRow = styled.div`
    margin: ${ContentSpacing.space8} 0 ${ContentSpacing.space16};
`;

const SizeLabel = styled(Body2)`
    display: inline-block;
    ${sizeLabelTheme};
`;

const Label = styled(Body2)`
    display: inline-block;
    ${sizePickerLabelTheme};
`;

const StyledColumn = styled(Column)`
    padding: 0;

    .slick-track {
        width: inherit !important;
        display: flex;
        justify-content: center;
    }
    .slick-slide {
        width: 50% !important;
    }
`;

const ProductSizeWrapper = styled.div`
    display: flex;
    flex-direction: column;
    [dir='ltr'] & {
        padding-left: ${ContentSpacing.space20};
    }
    [dir='rtl'] & {
        padding-right: ${ContentSpacing.space20};
    }
`;

const StyledSizeBoxesWrapper = styled.div`
    display: flex;
`;

const PriceAndSizeWrapper = styled.div`
    display: flex;
`;

export const ProductSizePicker = translate(['sizeLabel', 'selectASizeLabel'])(
    observer((props: ISizePicker) => {
        const sizePickerClassNames = classes('size-picker-wrapper');
        const sizePickerDropdownClassName = classes('size-picker-dropdown');
        const sizePickerLabelClassName = classes('size-picker-label');
        const priceAndSizeWrapperClassName = classes('price-and-size');
        const sizeClassName = classes('size');
        const formattedPriceClassName = classes('formatted-price');

        const singleSizeClassName = classnames(
            sizePickerClassNames,
            'elc-single-size',
            'js-single-size'
        );

        const { sizeLabel, selectASizeLabel } = props.translations as IProductSizePickerLabels;
        const { sizes, selectedSize, onSelect, useDropup, options, productId } = props;

        const renderPriceAndSize = (price: string, size: string) => {
            return (
                <PriceAndSizeWrapper className={priceAndSizeWrapperClassName}>
                    <div className={formattedPriceClassName}>{price}</div>
                    <div className={sizeClassName}>{size}</div>
                </PriceAndSizeWrapper>
            );
        };

        if (options && options.type === SIZE_PICKER_TYPE.DROPDOWN) {
            const handleOnChangeDropDown = (inputName: string, val: string) => {
                if (onSelect) {
                    onSelect(val, productId);
                }
            };

            let dropdownOptions: {
                label: string;
                value: string;
                htmlContent?: JSX.Element;
            }[] = [];
            const selectedValue = selectedSize.value;

            if (sizes && sizes.length > 1) {
                dropdownOptions = sizes.map(size => {
                    let label: string = size.value;
                    const showPrice = options && options.showPrice;
                    const hidePricePerUnit = options && options.hidePricePerUnit;
                    let htmlContent;

                    if (showPrice && size.parentSku) {
                        const priceFormatted = size.parentSku.prices[0].priceFormatted;
                        const dropdownPrice = hidePricePerUnit
                            ? `${priceFormatted} ${size.parentSku.formattedPrice}`
                            : priceFormatted;
                        const dropdownSize = size.value;

                        htmlContent = renderPriceAndSize(dropdownPrice, dropdownSize);
                        label = hidePricePerUnit
                            ? `${priceFormatted} ${dropdownSize}`
                            : `${priceFormatted} ${size.parentSku.formattedPrice} ${dropdownSize}`;
                    }

                    return { label, value: size.value, htmlContent };
                });
            }

            return sizes && sizes.length > 1 ? (
                <StyledColumn sm={6} lg={6} className={sizePickerClassNames}>
                    <Subtitle2 className="elc-select-a-size js-select-a-size">
                        {selectASizeLabel}
                    </Subtitle2>
                    <SizesDropdown
                        options={dropdownOptions}
                        name={'sizeNames'}
                        selectedValue={selectedValue}
                        dataTestId={PRODUCT_SIZE_DROPDOWN}
                        onChange={handleOnChangeDropDown}
                        useDropup={useDropup}
                        className={sizePickerDropdownClassName}
                    />
                </StyledColumn>
            ) : (
                <StyledColumn sm={6} lg={6} className={singleSizeClassName}>
                    <SizeDropdown className={sizePickerLabelClassName}>
                        <LabelDropdown data-test-id={PRODUCT_SIZE_DROPDOWN}>
                            {options && options.showPrice && selectedSize.parentSku
                                ? renderPriceAndSize(
                                      selectedSize.parentSku.prices[0].priceFormatted,
                                      selectedValue
                                  )
                                : `${selectedValue}`}
                        </LabelDropdown>
                    </SizeDropdown>
                </StyledColumn>
            );
        }

        const responsiveSettings = {
            responsive: [
                {
                    breakpoint: Breakpoints.desktop,
                    settings: {
                        slidesToShow: 7,
                        slidesToScroll: 7
                    }
                }
            ]
        };

        const onChipThumbnailSelect = (sizeInstance: Size) => () => {
            if (onSelect) {
                onSelect(sizeInstance.value, productId);
            }
        };

        const getSizesBoxes = (sizesToDisplay: Size[], isMobile?: boolean) => {
            return sizesToDisplay.map((size, index) => {
                const sizeInstance = new Size(size);
                const imageInstance = sizeInstance.smallImage
                    ? sizeInstance.smallImage
                    : new Asset();

                // don't display thumbnails if there are no thumbnails
                // don't display thumbnails on mobile (global business rule)
                const hideChipThumbnails = !sizeInstance.smallImage || isMobile;

                const productSizeBoxProps = {
                    size,
                    isSelected: sizeInstance.equals(selectedSize),
                    key: index,
                    onSelect,
                    productId
                };

                if (
                    options &&
                    options.type === SIZE_PICKER_TYPE.CHIPS_THUMBNAIL &&
                    !hideChipThumbnails
                ) {
                    return (
                        <ProductSizeWrapper
                            onClick={onChipThumbnailSelect(sizeInstance)}
                            key={index}
                        >
                            <ProductImages
                                imgPath={imageInstance.src}
                                altText={imageInstance.alt}
                            />
                            <ProductSizeBox {...productSizeBoxProps} />
                        </ProductSizeWrapper>
                    );
                }

                // by default, render just size chips with no thumbnail
                return <ProductSizeBox {...productSizeBoxProps} />;
            });
        };

        const carouselSettings = options && options.carousel;

        const sliderSettings: ISliderSettings = {
            dots: false,
            arrows: false,
            infinite: false,
            speed: 500,
            ...responsiveSettings,
            ...carouselSettings
        };

        const renderSizes = (items: Size[]) => {
            const selectedSizeClassName = 'elc-selected-size js-selected-size';
            const hasMultipleSizes = sizes && sizes.length > 1;
            const renderThumbnails = hasMultipleSizes || (options && options.showThumbIfSingleSize);

            return (
                <>
                    <SpaceRow>
                        {hasMultipleSizes && (
                            <SizeLabel className="elc-selected-size-label">{sizeLabel}: </SizeLabel>
                        )}
                        <Label className={selectedSizeClassName}>{selectedSize.value}</Label>
                    </SpaceRow>
                    {renderThumbnails && (
                        <StyledSizeBoxesWrapper>
                            <Desktop minWidth={Breakpoints.desktop}>
                                {() => (
                                    <SliderView
                                        sliderSettings={sliderSettings}
                                        items={getSizesBoxes(items, false)}
                                    />
                                )}
                            </Desktop>
                            <Mobile maxWidth={Breakpoints.desktop - 1}>
                                {() => (
                                    <SliderView
                                        sliderSettings={sliderSettings}
                                        items={getSizesBoxes(items, true)}
                                    />
                                )}
                            </Mobile>
                        </StyledSizeBoxesWrapper>
                    )}
                </>
            );
        };

        const filteredSizes = sizes.filter(size => size.value !== '');

        return (
            <StyledColumn className={sizePickerClassNames}>
                {renderSizes(filteredSizes)}
            </StyledColumn>
        );
    })
);

export default ProductSizePicker;
